import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase";
import { doc, getDoc, setDoc, collection, query, where, getDocs } from "firebase/firestore";
import Header from "../components/Header";
import Footer from "../components/Footer";
import axios from "axios";
import { Loader2 } from "lucide-react"; // Import the spinner icon

const UserProfilePage = () => {
  const [userData, setUserData] = useState({
    onboarding: null,
    health: null,
    meals: null,
  });
  const [loading, setLoading] = useState(true);
  const [generatingTips, setGeneratingTips] = useState(false);
  const [tips, setTips] = useState([]); // Initialize as empty array instead of null
  const [showTipsPopup, setShowTipsPopup] = useState(false);

  const [bmi, setBmi] = useState(null);
  const [formData, setFormData] = useState({
    weight: userData.onboarding?.weight || 0,
    height: userData.onboarding?.height || 0,
    weightUnit: userData.onboarding?.weightUnit || "kg",
    heightUnit: userData.onboarding?.heightUnit || "cm",
  });


  useEffect(() => {
    const fetchUserData = async (user) => {
      try {
        const onboardingDoc = await getDoc(doc(db, "onboardingData", user.uid));
        const healthDoc = await getDoc(doc(db, "healthData", user.uid));
        const mealLogsDoc = await getDoc(doc(db, "mealLogs", user.uid));

        setUserData({
          onboarding: onboardingDoc.exists() ? onboardingDoc.data() : null,
          health: healthDoc.exists() ? healthDoc.data() : null,
          meals: mealLogsDoc.exists() ? mealLogsDoc.data() : null,
        });
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
      setLoading(false);
    };

    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        fetchUserData(user);
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);
  const fetchHealthTips = async (userId) => {
    const q = query(collection(db, "healthTips"), where("userId", "==", userId));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      querySnapshot.forEach((doc) => {
        setTips(doc.data().tips);
      });
    }
  };

  const calculateBMI = () => {
    if (formData.weight && formData.height) {
      const weightInKg = formData.weightUnit === "kg" ? formData.weight : formData.weight / 2.20462;
      const heightInMeters = formData.heightUnit === "cm" ? formData.height / 100 : formData.height * 0.3048;
      const calculatedBmi = (weightInKg / (heightInMeters * heightInMeters)).toFixed(2);
      setBmi(calculatedBmi);
    } else {
      setBmi(null);
    }
  };

  useEffect(() => {
    if (userData.onboarding) {
      setFormData({
        weight: userData.onboarding.weight,
        height: userData.onboarding.height,
        weightUnit: userData.onboarding.weightUnit,
        heightUnit: userData.onboarding.heightUnit,
      });
    }
  }, [userData.onboarding]);

  useEffect(() => {
    calculateBMI();
  }, [formData.weight, formData.height, formData.weightUnit, formData.heightUnit]);

  const generatePrompt = () => {
    const currentTime = new Date();
    
    const comprehensiveTipGeneration = () => {
      const tips = [];
      const onboarding = userData.onboarding;
  
      // Substance Use Recommendations
      if (onboarding?.sleep?.caffeinatedDrinks > 2) {
        tips.push(`Caffeine Intake Management: Limit caffeine to max 2-3 drinks per day. Your current intake of ${onboarding.sleep.caffeinatedDrinks} drinks may disrupt sleep patterns and increase anxiety. Consider replacing afternoon/evening caffeinated drinks with herbal teas or decaf alternatives.`);
      }
  
      if (onboarding?.substanceUse?.alcoholAmount === '10_plus') {
        tips.push(`Alcohol Consumption Risk: Your reported alcohol intake suggests potential health risks. Aim to reduce to recommended limits: max 1 drink/day for women, 2 for men. Consider professional guidance for gradual reduction strategies.`);
      }
  
      if (onboarding?.substanceUse?.recreationalDrugs === 'daily') {
        tips.push(`Substance Use Intervention: Daily recreational drug use requires immediate professional medical consultation. Seek comprehensive addiction support through specialized counseling and medical oversight.`);
      }
  
      // Mental Health Recommendations
      const mentalHealthRisks = [
        onboarding?.mentalHealth?.feelingDown === 'several_days',
        onboarding?.mentalHealth?.littleInterest === 'several_days',
        onboarding?.mentalHealth?.stressLevel === 'high'
      ].filter(Boolean);
  
      if (mentalHealthRisks.length > 1) {
        tips.push(`Mental Wellness Intervention: Multiple indicators suggest potential mental health challenges. Implement a holistic approach:
  - Practice daily mindfulness/meditation (10-15 minutes)
  - Establish consistent sleep schedule
  - Engage in regular physical activity
  - Consider professional counseling for comprehensive mental health assessment`);
      }
  
      // Sleep Optimization
      if (onboarding?.sleep?.sleepSixToEight === 'rarely') {
        tips.push(`Sleep Health Critical: Your sleep pattern indicates chronic sleep deficit. Prioritize:
  - Consistent sleep-wake cycle
  - Create dark, cool sleeping environment
  - Limit screen time 1 hour before bedtime
  - Target 7-8 hours of quality sleep nightly
  - Consider sleep hygiene consultation`);
      }
  
      // Relationship and Social Wellness
      if (onboarding?.relationships?.feelIsolated === 'often') {
        tips.push(`Social Connection Strategy: Addressing isolation is crucial for mental and physical health.
  - Join community groups aligned with interests
  - Volunteer in local organizations
  - Develop structured social interaction plan
  - Consider support groups for shared experiences
  - Practice active communication skills`);
      }
  
      // Nutritional and Dietary Optimization
      const nutritionalInterventions = [];
      if (!onboarding?.diet?.fruitServings) {
        nutritionalInterventions.push('Increase daily fruit intake to 2-3 servings');
      }
      if (!onboarding?.diet?.vegetableServings) {
        nutritionalInterventions.push('Aim for 4-5 vegetable servings daily');
      }
      if (onboarding?.diet?.ultraProcessedFood) {
        nutritionalInterventions.push('Reduce ultra-processed food consumption by 50%');
      }
  
      if (nutritionalInterventions.length) {
        tips.push(`Nutrition Optimization Targets:
  ${nutritionalInterventions.map(tip => `- ${tip}`).join('\n')}`);
      }
  
      // Physical Activity Recommendations
      if (onboarding?.physicalActivity?.walking === 'none') {
        tips.push(`Sedentary Lifestyle Intervention: Gradually increase physical activity.
  - Start with 15-minute daily walks
  - Progress to 30 minutes moderate activity 5 days/week
  - Incorporate strength training 2-3 times weekly
  - Use step tracking for motivation`);
      }
  
      // Symptoms and Health Conditions Management
      if (onboarding?.symptoms?.includes('Abdominal Pain')) {
        tips.push(`Symptom Management - Abdominal Pain:
  - Maintain food diary to identify potential triggers
  - Consult healthcare provider for comprehensive evaluation
  - Consider anti-inflammatory diet
  - Practice stress reduction techniques`);
      }
  
      // Digital Wellness
      if (onboarding?.socialMediaAddiction?.neglectActivities) {
        tips.push(`Digital Wellness Reset:
  - Implement strict daily screen time limits
  - Use app blockers during work/personal time
  - Replace digital time with meaningful activities
  - Practice periodic digital detox
  - Develop offline hobby or skill`);
      }
  
      return tips;
    };
  
    return {
      role: "user",
      content: `Advanced Personalized Health Optimization Protocol
  
  COMPREHENSIVE HEALTH RECOMMENDATIONS
  
  Generate up to 5 highly personalized, evidence-based health optimization strategies that:
  1. Address specific individual health risks
  2. Provide actionable, measurable interventions
  3. Integrate multisystem health approach
  4. Leverage comprehensive individual health data
  
  GENERATED RECOMMENDATIONS:
  ${comprehensiveTipGeneration().map((tip, index) => `${index + 1}. ${tip}`).join('\n\n')}
  
  Recommendation Requirements:
  - Clinical precision
  - Specific numerical targets
  - Clear rationale
  - Preventative and rehabilitative strategies`
    };
  };
  const OPENAI_API_KEY = "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"; // Replace with your actual API key

  
  const generateHealthTips = async () => {
    setGeneratingTips(true);
    const currentTime = new Date();
    
    try {
      const messages = [
        {
          role: "system",
          content: `You are an advanced health advisor. Generate creative and varied health tips 
          that are highly specific to the user's context and current situation. 
          Each tip should be unique and actionable.`
        },
        generatePrompt(),
      ];
  
      const response = await axios.post(
        "https://api.openai.com/v1/chat/completions",
        {
          model: "gpt-3.5-turbo",
          messages,
          max_tokens: 500,
          temperature: 0.9,
          frequency_penalty: 1.0,
          presence_penalty: 1.0,
          top_p: 0.95
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${OPENAI_API_KEY}`, // Use the constant
          },
          
        }
      );
  
      const generatedContent = response.data.choices[0].message.content;
      
      const generatedTips = generatedContent
        .split(/\d+\.|\n\n/)
        .map(tip => tip.trim())
        .filter(tip => tip.length > 0)
        .map(tip => tip.replace(/^[-•\s]+/, ''));
  
      setTips(generatedTips);
  
      // Save to Firebase (but don't depend on it for display)
      const user = auth.currentUser;
      if (user) {
        await setDoc(doc(db, "healthTips", user.uid), {
          userId: user.uid,
          tips: generatedTips,
          timestamp: new Date().toISOString(),
          metadata: {
            timeOfDay: currentTime.getHours() < 12 ? 'Morning' : currentTime.getHours() < 17 ? 'Afternoon' : 'Evening',
            dayOfWeek: currentTime.toLocaleDateString('en-US', { weekday: 'long' }),
            date: currentTime.toLocaleDateString()
          }
        });
      }
    } catch (error) {
      console.error("Error generating health tips:", error);
    } finally {
      setGeneratingTips(false);
    }
  };
  const handleTipsButtonClick = async () => {
    setShowTipsPopup(true);
    setTips([]); // Clear existing tips before generating new ones
    await generateHealthTips();
  };
  const handleClosePopup = async () => {
    setShowTipsPopup(false);
    // Update the database with the latest tips when closing
    const user = auth.currentUser;
    if (user && tips) {
      try {
        await setDoc(doc(db, "healthTips", user.uid), {
          userId: user.uid,
          tips: tips,
          timestamp: new Date().toISOString(),
        });
      } catch (error) {
        console.error("Error updating tips in database:", error);
      }
    }
  };




  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gradient-to-br from-teal-500 to-blue-700 text-white">
        <div className="text-xl font-semibold">Loading...</div>
      </div>
    );
  }

  const TipsPopup = ({ tips, generatingTips, setShowTipsPopup }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
      <div className="bg-white p-6 rounded-xl shadow-xl w-11/12 sm:w-3/4 md:w-2/3 lg:w-1/3 max-h-[80vh] overflow-hidden">
        <h3 className="text-2xl font-bold text-center mb-4">Health Tips from AI</h3>
        <div className="space-y-4 overflow-y-auto max-h-[60vh]">
          {generatingTips ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-2" />
              <p className="text-lg text-gray-600">Generating your personalized health tips...</p>
            </div>
          ) : tips.length > 0 ? (
            tips.map((tip, index) => (
              <div key={index} className="bg-gray-50 p-4 rounded-lg">
                <p className="text-lg text-gray-700">{tip}</p>
              </div>
            ))
          ) : (
            <div className="flex justify-center items-center py-8">
              <p className="text-lg text-gray-500">Preparing to generate your health tips...</p>
            </div>
          )}
        </div>
        <button
          onClick={() => setShowTipsPopup(false)}
          aria-label="Close tips popup"
          className="mt-6 bg-red-500 hover:bg-red-600 transition-colors text-white py-2 px-4 rounded-full w-full"
        >
          Close
        </button>
      </div>
    </div>
  );
  

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-teal-500 to-blue-700">
      <Header />
      <main className="flex-grow container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-white mb-8 text-center">Your Health Dashboard</h1>
        <div className="grid gap-8">
          
          {/* Personal Information Section */}
              <div className="bg-white rounded-xl shadow-xl p-6 text-gray-800">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-blue-600">Personal Information</h2>
                  {userData.onboarding?.healthGoals && (
                    <span className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium">
                      Goal: {userData.onboarding.healthGoals}
                    </span>
                  )}
                </div>
                {userData.onboarding ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="font-semibold text-gray-600">Weight</p>
                      <p className="text-xl">{userData.onboarding.weight} {userData.onboarding.weightUnit}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="font-semibold text-gray-600">Height</p>
                      <p className="text-xl">{userData.onboarding.height} {userData.onboarding.heightUnit}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="font-semibold text-gray-600">BMI</p>
                      <p className="text-xl">{bmi ? bmi : "No BMI data available"}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="font-semibold text-gray-600">Waist</p>
                      <p className="text-xl">{userData.onboarding.waist || "N/A"} cm</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg col-span-1 lg:col-span-2">
                      <p className="font-semibold text-gray-600">Dietary Habits</p>
                      <p className="text-xl">{userData.onboarding.dietaryHabits || "Not specified"}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg col-span-1 lg:col-span-2">
                      <p className="font-semibold text-gray-600">Health Conditions</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {userData.onboarding.healthConditions?.map((condition, index) => (
                          <span key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                            {condition}
                          </span>
                        )) || <p className="text-sm text-gray-500">None</p>}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-gray-500">No personal information available</p>
                )}
                {/* Tips Button 
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={handleTipsButtonClick}
                    className="bg-gradient-to-r from-blue-500 to-teal-500 text-white font-medium py-3 px-6 rounded-full shadow-md hover:shadow-lg hover:opacity-90 transition duration-200"
                  >
                    Tips from Health-AI
                  </button>
                </div>*/}
              </div>


          {/* Middle Section: Health Tracking */}
          <div className="grid md:grid-cols-2 gap-8">
            {/* Sleep History */}
            <div className="bg-white rounded-xl shadow-xl p-6 text-gray-800 max-h-96 overflow-y-auto">
              <h2 className="text-2xl font-bold mb-6 text-blue-600">Sleep History</h2>
              {userData.health?.sleepPatterns ? (
                <div className="overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left py-3 px-4 font-semibold text-gray-600">Date</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-600">Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userData.health.sleepPatterns.map((sleep, index) => (
                        <tr key={index} className="border-b hover:bg-gray-50 transition-colors">
                          <td className="py-3 px-4">{sleep.date}</td>
                          <td className="text-right py-3 px-4">{sleep.hours}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-gray-500">No sleep data available</p>
              )}
            </div>

            {/* Weight History */}
            <div className="bg-white rounded-xl shadow-xl p-6 text-gray-800 max-h-96 overflow-y-auto">
              <h2 className="text-2xl font-bold mb-6 text-blue-600">Weight History</h2>
              {userData.health?.weightTrends ? (
                <div className="overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left py-3 px-4 font-semibold text-gray-600">Date</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-600">Weight (kg)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userData.health.weightTrends.map((weight, index) => (
                        <tr key={index} className="border-b hover:bg-gray-50 transition-colors">
                          <td className="py-3 px-4">{weight.date}</td>
                          <td className="text-right py-3 px-4">{weight.weight}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-gray-500">No weight data available</p>
              )}
            </div>
          </div>

          {/* Bottom Section: Meal Logs */}
          <div className="bg-white rounded-xl shadow-xl p-6 text-gray-800 max-h-96 overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6 text-blue-600">Recent Meals</h2>
            {userData.meals?.mealLog ? (
              <div className="space-y-6">
                {Object.entries(userData.meals.mealLog)
                  .slice(-3)
                  .map(([date, meals]) => (
                    <div key={date} className="border-b pb-6 last:border-b-0 last:pb-0">
                      <p className="font-semibold text-lg text-gray-700 mb-4">{date}</p>
                      <div className="grid md:grid-cols-3 gap-4">
                        {meals.map((meal, index) => (
                          <div key={index} className="bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-colors">
                            <p className="font-medium text-gray-800">{meal.label}</p>
                            <p className="text-sm text-gray-600 mt-1">
                              {meal.grams}g - {meal.calories} calories
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-center text-gray-500">No meal logs available</p>
            )}
          </div>
        </div>
      </main>
      {showTipsPopup && (
  <TipsPopup 
    tips={tips} 
    generatingTips={generatingTips} 
    setShowTipsPopup={setShowTipsPopup} 
  />
)}
      <Footer />
    </div>
  );
};

export default UserProfilePage;

